/**
 * Prepared Turntable — frontend manifest loader / resolver (Stage 5C2b).
 *
 * Isolated data/loading layer only. Does NOT:
 * - fetch frame bodies
 * - activate prepared media on cards
 * - replace Nivoda v360
 * - mutate mapped diamond items
 *
 * Callers must invoke loadPreparedTurntableManifest() explicitly.
 * Creating / gating availability never network-fetches.
 */
(function (global) {
  "use strict";

  /** @type {Map<string, object>} */
  var successCache = new Map();
  /** @type {Map<string, Promise<object>>} */
  var inflight = new Map();

  var SUPPORTED_MANIFEST_VERSION = 1;

  function unavailable(reason) {
    return { available: false, reason: reason || "unavailable" };
  }

  function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
  }

  /**
   * Availability gate — never fetches.
   * True only when registry metadata says available and has a public manifestUrl.
   */
  function isPreparedTurntableAvailable(preparedTurntable) {
    return !!(
      preparedTurntable &&
      preparedTurntable.available === true &&
      isNonEmptyString(preparedTurntable.manifestUrl)
    );
  }

  /**
   * Resolve a single published frame to an absolute URL.
   * Published R2 manifests use relative `ref` (e.g. "frames/000.webp").
   * Prefers `ref` resolved against manifestUrl; falls back to absolute `url`
   * when present (local PoC fixture shape). Returns null if neither is usable.
   */
  function resolveFrameUrl(frame, manifestUrl) {
    if (frame && isNonEmptyString(frame.ref) && isNonEmptyString(manifestUrl)) {
      // Use global.URL (window.URL in browsers) so Node/vm test sandboxes
      // can inject URL explicitly; never assume a free bare URL binding.
      var URLCtor = global.URL;
      if (typeof URLCtor !== "function") {
        return null;
      }
      try {
        return new URLCtor(frame.ref, manifestUrl).href;
      } catch (_) {
        return null;
      }
    }
    if (frame && isNonEmptyString(frame.url)) {
      return frame.url;
    }
    return null;
  }

  /**
   * Strict-enough parser for the Stage 4 published manifest.
   * Throws on schema failure (caught by the public loader).
   * Never mutates the input JSON.
   */
  function parsePublishedManifest(manifest, preparedTurntable, manifestUrl) {
    if (!manifest || typeof manifest !== "object") {
      throw new Error("schema");
    }
    if (manifest.version !== SUPPORTED_MANIFEST_VERSION) {
      throw new Error("unsupported_version");
    }
    if (manifest.status !== "ready") {
      throw new Error("schema");
    }

    var presentation = manifest.presentation;
    if (!presentation || typeof presentation !== "object") {
      throw new Error("schema");
    }

    var frameCount = presentation.frameCount;
    if (typeof frameCount !== "number" || !isFinite(frameCount) || frameCount <= 0) {
      throw new Error("schema");
    }
    if (!Number.isInteger(frameCount)) {
      throw new Error("schema");
    }

    var rawFrames = presentation.frames;
    if (!Array.isArray(rawFrames) || rawFrames.length !== frameCount) {
      throw new Error("schema");
    }

    var topIndex =
      presentation.topIndex != null ? presentation.topIndex : 0;
    if (typeof topIndex !== "number" || !Number.isInteger(topIndex)) {
      throw new Error("schema");
    }
    if (topIndex < 0 || topIndex >= frameCount) {
      throw new Error("schema");
    }

    // Deterministic order by presentation index `i`.
    var sorted = rawFrames.slice().sort(function (a, b) {
      return (a && a.i) - (b && b.i);
    });

    var frames = [];
    for (var idx = 0; idx < sorted.length; idx += 1) {
      var frame = sorted[idx];
      if (!frame || typeof frame !== "object") {
        throw new Error("schema");
      }
      if (frame.i !== idx) {
        throw new Error("schema");
      }
      if (typeof frame.sourceIndex !== "number" || !Number.isInteger(frame.sourceIndex)) {
        throw new Error("schema");
      }

      var absoluteUrl = resolveFrameUrl(frame, manifestUrl);
      if (!absoluteUrl) {
        throw new Error("schema");
      }

      var normalizedFrame = {
        i: frame.i,
        sourceIndex: frame.sourceIndex,
        url: absoluteUrl,
      };
      // Preserve published `ref` when present; do not invent one.
      if (isNonEmptyString(frame.ref)) {
        normalizedFrame.ref = frame.ref;
      }
      frames.push(normalizedFrame);
    }

    var assetKey = null;
    if (isNonEmptyString(preparedTurntable.assetKey)) {
      assetKey = preparedTurntable.assetKey;
    } else if (isNonEmptyString(manifest.assetKey)) {
      assetKey = manifest.assetKey;
    }
    if (!assetKey) {
      throw new Error("schema");
    }
    // Defensive: registry meta and published manifest must agree when both set.
    if (
      isNonEmptyString(preparedTurntable.assetKey) &&
      isNonEmptyString(manifest.assetKey) &&
      preparedTurntable.assetKey !== manifest.assetKey
    ) {
      throw new Error("schema");
    }

    var version = null;
    if (isNonEmptyString(preparedTurntable.version)) {
      version = preparedTurntable.version;
    } else if (isNonEmptyString(manifest.pipelineVersion)) {
      version = manifest.pipelineVersion;
    }
    if (!version) {
      throw new Error("schema");
    }
    if (
      isNonEmptyString(preparedTurntable.version) &&
      isNonEmptyString(manifest.pipelineVersion) &&
      preparedTurntable.version !== manifest.pipelineVersion
    ) {
      throw new Error("schema");
    }

    var result = {
      available: true,
      assetKey: assetKey,
      version: version,
      manifestUrl: manifestUrl,
      frameCount: frameCount,
      topIndex: topIndex,
      frames: frames,
    };

    // Optional presentation fields from the published schema only.
    if (typeof presentation.format === "string") {
      result.format = presentation.format;
    }
    if (typeof presentation.width === "number") {
      result.width = presentation.width;
    }
    if (typeof presentation.height === "number") {
      result.height = presentation.height;
    }
    if (isNonEmptyString(manifest.pipelineVersion)) {
      result.pipelineVersion = manifest.pipelineVersion;
    }

    return result;
  }

  function classifyParseError(err) {
    var msg = err && err.message ? String(err.message) : "";
    if (msg === "unsupported_version") return "unsupported_version";
    return "schema";
  }

  /**
   * Explicit manifest load. Soft-fails: never throws to callers.
   *
   * @param {object|null|undefined} preparedTurntable
   *   Mapped item.preparedTurntable from Stage 5C2a.
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] optional; forwarded to fetch when provided
   * @param {typeof fetch} [options.fetch] optional inject for tests
   * @returns {Promise<{available:boolean, reason?:string, ...}>}
   */
  function loadPreparedTurntableManifest(preparedTurntable, options) {
    options = options || {};

    if (!isPreparedTurntableAvailable(preparedTurntable)) {
      return Promise.resolve(unavailable("unavailable"));
    }

    var manifestUrl = String(preparedTurntable.manifestUrl).trim();

    if (successCache.has(manifestUrl)) {
      return Promise.resolve(successCache.get(manifestUrl));
    }

    if (inflight.has(manifestUrl)) {
      return inflight.get(manifestUrl);
    }

    var fetchFn = options.fetch || global.fetch;
    if (typeof fetchFn !== "function") {
      return Promise.resolve(unavailable("network"));
    }

    var fetchInit = {
      method: "GET",
      credentials: "omit",
      // Public CDN manifests are immutable; default browser cache is fine.
      // Do not send cookies / auth headers.
    };
    if (options.signal) {
      fetchInit.signal = options.signal;
    }

    var promise = Promise.resolve()
      .then(function () {
        return fetchFn(manifestUrl, fetchInit);
      })
      .then(function (res) {
        if (!res || typeof res.ok !== "boolean") {
          return unavailable("http");
        }
        if (!res.ok) {
          return unavailable("http");
        }
        return Promise.resolve()
          .then(function () {
            return res.json();
          })
          .then(
            function (json) {
              try {
                var normalized = parsePublishedManifest(
                  json,
                  preparedTurntable,
                  manifestUrl
                );
                successCache.set(manifestUrl, normalized);
                return normalized;
              } catch (err) {
                return unavailable(classifyParseError(err));
              }
            },
            function () {
              return unavailable("json");
            }
          );
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") {
          return unavailable("aborted");
        }
        return unavailable("network");
      })
      .then(function (result) {
        inflight.delete(manifestUrl);
        return result;
      });

    inflight.set(manifestUrl, promise);
    return promise;
  }

  /** Test / recovery helper — clears in-memory success + in-flight maps. */
  function clearPreparedTurntableManifestCache() {
    successCache.clear();
    inflight.clear();
  }

  global.DiamondPreparedTurntableLoader = {
    isPreparedTurntableAvailable: isPreparedTurntableAvailable,
    loadPreparedTurntableManifest: loadPreparedTurntableManifest,
    clearPreparedTurntableManifestCache: clearPreparedTurntableManifestCache,
    // Exposed for focused unit checks (same seam as PoC resolveFrameUrl).
    resolveFrameUrl: resolveFrameUrl,
    SUPPORTED_MANIFEST_VERSION: SUPPORTED_MANIFEST_VERSION,
  };
})(typeof window !== "undefined" ? window : globalThis);
